import { problemDetailsSchema } from '@jaf/contracts';
import { isAxiosError } from 'axios';

/** Origem da falha: resposta HTTP, rede, tempo esgotado ou resposta fora do contrato. */
export type ApiErrorKind = 'http' | 'network' | 'timeout' | 'contract';

/** Erro normalizado da API (problem+json validado), usado por toda a UI. */
export class ApiError extends Error {
  readonly kind: ApiErrorKind;
  readonly status: number;
  readonly title: string;
  readonly detail: string | undefined;
  readonly code: string | undefined;
  readonly traceId: string | undefined;
  readonly fieldErrors: Readonly<Record<string, string>>;

  constructor(init: {
    kind: ApiErrorKind;
    status: number;
    title: string;
    detail?: string | undefined;
    code?: string | undefined;
    traceId?: string | undefined;
    fieldErrors?: Record<string, string>;
  }) {
    super(init.detail ?? init.title);
    this.name = 'ApiError';
    this.kind = init.kind;
    this.status = init.status;
    this.title = init.title;
    this.detail = init.detail;
    this.code = init.code;
    this.traceId = init.traceId;
    this.fieldErrors = init.fieldErrors ?? {};
  }

  /** Indica falha de conectividade ou indisponibilidade do servidor. */
  get isUnavailable(): boolean {
    return this.kind === 'network' || this.kind === 'timeout' || this.status === 503;
  }
}

/** Converte qualquer falha (axios, rede, contrato) em ApiError. */
export function toApiError(error: unknown): ApiError {
  if (error instanceof ApiError) return error;
  if (isAxiosError(error)) {
    if (error.code === 'ECONNABORTED' || error.code === 'ETIMEDOUT') {
      return new ApiError({ kind: 'timeout', status: 0, title: 'Tempo de resposta esgotado' });
    }
    if (error.response === undefined) {
      return new ApiError({ kind: 'network', status: 0, title: 'Sem conexão com o servidor' });
    }
    const problem = problemDetailsSchema
      .extend({ code: problemDetailsSchema.shape.detail })
      .safeParse(error.response.data);
    if (problem.success) {
      return new ApiError({
        kind: 'http',
        status: problem.data.status,
        title: problem.data.title,
        detail: problem.data.detail,
        code: problem.data.code,
        traceId: problem.data.traceId,
        fieldErrors: Object.fromEntries(
          (problem.data.errors ?? []).map((fieldError) => [fieldError.field, fieldError.message]),
        ),
      });
    }
    return new ApiError({
      kind: 'http',
      status: error.response.status,
      title: 'Erro inesperado do servidor',
    });
  }
  return new ApiError({ kind: 'contract', status: 0, title: 'Resposta inesperada do servidor' });
}
